// src/components/Sales/CreateSaleModal.jsx
import React, { useState, useEffect } from 'react';
import salesService from '../../services/salesService';

const CreateSaleModal = ({ isOpen, onClose, onSave, isSaving, inventories, categories }) => {
  // ========== STATE MANAGEMENT ==========
  const [allStockIn, setAllStockIn] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  
  // Cart items
  const [cart, setCart] = useState([]);
  
  // Payments
  const [payments, setPayments] = useState([]);
  
  // Product selection form
  const [productForm, setProductForm] = useState({
    inventoryId: '',
    categoryId: '',
    productId: '',
    qty: '1',
    unitPrice: ''
  });
  
  // Customer info
  const [customerInfo, setCustomerInfo] = useState({
    customerId: '',
    customerNumber: '',
    isCashCustomer: true
  });
  
  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    type: 'CASH',
    amount: '',
    chequeId: '',
    chequeHolder: '',
    expiryDate: '',
    chequeType: '',
    bankName: ''
  });

  // ========== LOAD DATA ==========
  useEffect(() => {
    if (isOpen) {
      loadAllStockIn();
      loadEmployees();
      loadCustomers();
      resetForm();
    }
  }, [isOpen]);

  const loadAllStockIn = async () => {
    try {
      console.log('Loading stocks...');
      const stocks = await salesService.getStocks();
      console.log('Raw stocks data:', stocks);
      
      // Log the structure of the first item to understand the data
      if (stocks && stocks.length > 0) {
        console.log('First stock item structure:', stocks[0]);
      }
      
      setAllStockIn(stocks || []);
    } catch (error) {
      console.error('Error loading stocks:', error);
      setAllStockIn([]);
    }
  };

  const loadEmployees = async () => {
    try {
      console.log('Loading employees...');
      const employeesData = await salesService.getEmployees();
      console.log('Employees data:', employeesData);
      setEmployees(employeesData || []);
    } catch (error) {
      console.error('Error loading employees:', error);
      setEmployees([]);
    }
  };

  const loadCustomers = async () => {
    try {
      console.log('Loading customers...');
      const customersData = await salesService.getCustomers();
      console.log('Customers data:', customersData);
      setCustomers(customersData || []);
    } catch (error) {
      console.error('Error loading customers:', error);
      setCustomers([]);
    }
  };

  const resetForm = () => {
    setCart([]);
    setPayments([]);
    setFilteredCategories([]);
    setFilteredProducts([]);
    setProductForm({ inventoryId: '', categoryId: '', productId: '', qty: '1', unitPrice: '' });
    setCustomerInfo({ customerId: '', customerNumber: '', isCashCustomer: true });
    setPaymentForm({ type: 'CASH', amount: '', chequeId: '', chequeHolder: '', expiryDate: '', chequeType: '', bankName: '' });
  };

  // ========== CASCADING FILTERS ==========
  const handleInventoryChange = (inventoryId) => {
    console.log('Inventory selected:', inventoryId);
    
    setProductForm(prev => ({ 
      ...prev, 
      inventoryId, 
      categoryId: '', 
      productId: '', 
      qty: '1', 
      unitPrice: '' 
    }));
    
    setFilteredProducts([]);
    
    if (!inventoryId) {
      setFilteredCategories([]);
      return;
    }

    // Extract unique categories for this inventory from stock data
    const categoriesMap = new Map();
    
    allStockIn.forEach(stockItem => {
      // Check if this stock item belongs to the selected inventory and has quantity
      // The category is nested under product.category
      if (stockItem.inventory?.id == parseInt(inventoryId) && 
          stockItem.qty > 0 && 
          stockItem.product?.category) {
        const category = stockItem.product.category;
        if (category?.id && !categoriesMap.has(category.id)) {
          categoriesMap.set(category.id, {
            id: category.id,
            name: category.name
          });
        }
      }
    });

    const categories = Array.from(categoriesMap.values());
    setFilteredCategories(categories);
    console.log('Categories for inventory', inventoryId, ':', categories);
  };

  const handleCategoryChange = (categoryId) => {
    console.log('Category selected:', categoryId);
    
    setProductForm(prev => ({ 
      ...prev, 
      categoryId, 
      productId: '', 
      qty: '1', 
      unitPrice: '' 
    }));

    if (!categoryId || !productForm.inventoryId) {
      setFilteredProducts([]);
      return;
    }

    // Filter products for selected inventory and category
    const products = allStockIn.filter(stockItem => {
      const matchesInventory = stockItem.inventory?.id == parseInt(productForm.inventoryId);
      const matchesCategory = stockItem.product?.category?.id == parseInt(categoryId);
      const hasStock = stockItem.qty > 0;
      
      return matchesInventory && matchesCategory && hasStock;
    });

    setFilteredProducts(products);
    console.log('Products for inventory', productForm.inventoryId, 'and category', categoryId, ':', products);
  };

  const handleProductChange = (productId) => {
    console.log('Product selected:', productId);
    
    const product = filteredProducts.find(p => p.id == productId);
    console.log('Selected product details:', product);
    
    setProductForm(prev => ({
      ...prev,
      productId,
      unitPrice: product?.pricePerUnit || '',
      qty: '1'
    }));
  };

  const getAvailableQty = () => {
    if (!productForm.productId) return 0;
    const product = filteredProducts.find(p => p.id == productForm.productId);
    return product?.qty || 0;
  };

  // ========== CART OPERATIONS ==========
  const addToCart = (e) => {
    e.preventDefault();
    
    if (!productForm.productId || !productForm.qty || !productForm.unitPrice) {
      alert('Please fill all product fields');
      return;
    }

    const product = filteredProducts.find(p => p.id == productForm.productId);
    if (!product) {
      alert('Product not found');
      return;
    }

    const requestedQty = parseInt(productForm.qty);
    if (requestedQty > product.qty) {
      alert(`Quantity exceeds available stock. Available: ${product.qty}`);
      return;
    }

    const newItem = {
      stockId: product.id,
      categoryId: product.product?.category?.id,
      inventoryId: product.inventory?.id,
      productName: product.product?.name || 'Unknown Product',
      categoryName: product.product?.category?.name || 'Unknown Category',
      qty: requestedQty,
      unitPrice: parseFloat(productForm.unitPrice),
      totalPrice: requestedQty * parseFloat(productForm.unitPrice)
    };
    
    console.log('Adding to cart:', newItem);
    setCart(prev => [...prev, newItem]);
    
    // Reset product selection but keep inventory and category
    setProductForm(prev => ({ 
      ...prev, 
      productId: '', 
      qty: '1', 
      unitPrice: '' 
    }));
  };

  const removeFromCart = (index) => {
    setCart(prev => prev.filter((_, i) => i !== index));
  };

  // ========== PAYMENT OPERATIONS ==========
  const addPayment = (e) => {
    e.preventDefault();
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      alert('Please enter valid amount');
      return;
    }

    setPayments(prev => [...prev, { ...paymentForm, amount: parseFloat(paymentForm.amount) }]);
    setPaymentForm(prev => ({ ...prev, amount: '' }));
  };

  const removePayment = (index) => {
    setPayments(prev => prev.filter((_, i) => i !== index));
  };

  // ========== CALCULATIONS ==========
  const getGrandTotal = () => cart.reduce((sum, item) => sum + item.totalPrice, 0);
  const getTotalPaid = () => payments.reduce((sum, p) => sum + p.amount, 0);
  const getBalance = () => getTotalPaid() - getGrandTotal();

  // ========== SUBMIT ==========
  const handleSubmit = () => {
    if (cart.length === 0) {
      alert('Please add items to cart');
      return;
    }

    if (payments.length === 0) {
      alert('Please add at least one payment');
      return;
    }

    // Backend allows only ONE payment method per sale
    const paymentMethod =
      payments[0].type === 'BANK'
        ? 'BANK TRANSFER'
        : payments[0].type;

    const payload = {
      sales: cart.map(item => ({
        stockId: item.stockId,
        categoryId: item.categoryId,
        inventoryId: item.inventoryId,
        customerId: customerInfo.isCashCustomer
          ? null
          : Number(customerInfo.customerId),
        qty: item.qty,
        price: item.qty * item.unitPrice,
        paymentMethod
      }))
    };

    console.log('FINAL API PAYLOAD:', JSON.stringify(payload, null, 2));
    onSave(payload);
  };

  if (!isOpen) return null;

  // Extract unique inventories from allStockIn
  const uniqueInventories = allStockIn.reduce((acc, stockItem) => {
    const inv = stockItem.inventory;
    if (inv?.id && !acc.find(item => item.id === inv.id)) {
      acc.push({ id: inv.id, name: inv.name });
    }
    return acc;
  }, []);

  return (
    <div className="modal-overlay">
      <div className="modal-content sales-modal-wide">
        <header className="modal-header">
          <h3>Create New Sale</h3>
          <button className="close-modal-btn" onClick={onClose} disabled={isSaving}>×</button>
        </header>

        <div className="modal-body sales-modal-body">
          {/* ========== LEFT COLUMN: Product Selection & Cart ========== */}
          <div className="sales-left-column">
            {/* Product Selection Form */}
            <div className="product-selection-card">
              <form onSubmit={addToCart}>
                <div className="form-row-sales">
                  <div className="form-col-sales">
                    <label>Inventory / Warehouse</label>
                    <select 
                      className="custom-select" 
                      value={productForm.inventoryId}
                      onChange={e => handleInventoryChange(e.target.value)}
                      required
                    >
                      <option value="">Select Inventory</option>
                      {(inventories && inventories.length > 0 ? inventories : uniqueInventories).map(inv => (
                        <option key={inv.id} value={inv.id}>{inv.name}</option>
                      ))}
                    </select>
                    {allStockIn.length === 0 && (
                      <small style={{color: '#999'}}>Loading stock data...</small>
                    )}
                  </div>

                  <div className="form-col-sales">
                    <label>Category</label>
                    <select 
                      className="custom-select" 
                      value={productForm.categoryId}
                      onChange={e => handleCategoryChange(e.target.value)}
                      required
                      disabled={!productForm.inventoryId}
                    >
                      <option value="">
                        {!productForm.inventoryId ? 'Select inventory first' : 'Select Category'}
                      </option>
                      {filteredCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                    {productForm.inventoryId && filteredCategories.length === 0 && (
                      <small style={{color: '#999'}}>No categories with stock in this inventory</small>
                    )}
                  </div>
                </div>

                <div className="form-row-sales">
                  <div className="form-col-sales">
                    <label>Product</label>
                    <select 
                      className="custom-select" 
                      value={productForm.productId}
                      onChange={e => handleProductChange(e.target.value)}
                      required
                      disabled={!productForm.categoryId}
                    >
                      <option value="">
                        {!productForm.categoryId ? 'Select category first' : 'Select Product'}
                      </option>
                      {filteredProducts.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.product?.name || 'Unknown'} (Available: {p.qty})
                        </option>
                      ))}
                    </select>
                    {productForm.categoryId && filteredProducts.length === 0 && (
                      <small style={{color: '#999'}}>No products with stock in this category</small>
                    )}
                    {productForm.productId && (
                      <small className="availability-text">
                        Available Quantity: <strong>{getAvailableQty()}</strong> | 
                        Price per Unit: <strong>Rs. {filteredProducts.find(p => p.id == productForm.productId)?.pricePerUnit}</strong>
                      </small>
                    )}
                  </div>

                  <div className="form-col-sales">
                    <label>Quantity</label>
                    <input 
                      type="number" 
                      className="custom-input" 
                      value={productForm.qty}
                      onChange={e => setProductForm({ ...productForm, qty: e.target.value })}
                      min="1"
                      max={getAvailableQty()}
                      required
                      disabled={!productForm.productId}
                    />
                  </div>

                  <div className="form-col-sales">
                    <label>Unit Price (Rs.)</label>
                    <input 
                      type="number" 
                      step="0.01"
                      className="custom-input" 
                      value={productForm.unitPrice}
                      onChange={e => setProductForm({ ...productForm, unitPrice: e.target.value })}
                      required
                      disabled={!productForm.productId}
                    />
                  </div>
                </div>

                <button 
                  type="submit" 
                  className="btn btn-add-item-full"
                  disabled={!productForm.productId}
                >
                  Add Item to Cart
                </button>
              </form>
            </div>

            {/* Cart Display */}
            <div className="cart-display-card">
              <h4 className="card-title">Items For Sale ({cart.length})</h4>
              <div className="cart-scroll-wrapper">
                {cart.length === 0 ? (
                  <div className="empty-state">Start adding items to the cart.</div>
                ) : (
                  <table className="mini-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Category</th>
                        <th>Unit Price</th>
                        <th>Quantity</th>
                        <th>Total Price</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cart.map((item, index) => (
                        <tr key={index}>
                          <td>{item.productName}</td>
                          <td>{item.categoryName}</td>
                          <td>Rs. {item.unitPrice.toFixed(2)}</td>
                          <td>{item.qty}</td>
                          <td>Rs. {item.totalPrice.toFixed(2)}</td>
                          <td>
                            <button className="btn-remove-mini" onClick={() => removeFromCart(index)} type="button">×</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {/* ========== RIGHT COLUMN: Customer & Payment ========== */}
          <div className="sales-right-column">
            {/* Customer Info */}
            <div className="customer-info-card">
              <div className="form-row-sales">
                <div className="form-col-sales">
                  <label>Customer</label>
                  <select 
                    className="custom-select"
                    value={customerInfo.isCashCustomer ? 'cash' : customerInfo.customerId}
                    onChange={e => {
                      if (e.target.value === 'cash') {
                        setCustomerInfo({ ...customerInfo, isCashCustomer: true, customerId: '' });
                      } else {
                        setCustomerInfo({ ...customerInfo, isCashCustomer: false, customerId: e.target.value });
                      }
                    }}
                  >
                    <option value="cash">Cash Customer</option>
                    {customers && customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-row-sales">
                <div className="form-col-sales">
                  <label>Customer Number</label>
                  <input 
                    type="text" 
                    className="custom-input"
                    value={customerInfo.customerNumber}
                    onChange={e => setCustomerInfo({ ...customerInfo, customerNumber: e.target.value })}
                    placeholder="Optional"
                  />
                </div>

                <div className="form-col-sales grand-total-col">
                  <label>Grand Total</label>
                  <div className="grand-total-display">Rs. {getGrandTotal().toFixed(2)}</div>
                </div>
              </div>
            </div>

            {/* Payment Section */}
            <div className="payment-card">
              <h4 className="card-title">Add Payments</h4>
              <form onSubmit={addPayment}>
                <div className="payment-type-tabs">
                  <button 
                    type="button"
                    className={`payment-tab-btn ${paymentForm.type === 'CASH' ? 'active' : ''}`}
                    onClick={() => setPaymentForm({ ...paymentForm, type: 'CASH' })}
                  >
                    Cash
                  </button>
                  <button 
                    type="button"
                    className={`payment-tab-btn ${paymentForm.type === 'BANK' ? 'active' : ''}`}
                    onClick={() => setPaymentForm({ ...paymentForm, type: 'BANK' })}
                  >
                    Bank Transfer
                  </button>
                  <button 
                    type="button"
                    className={`payment-tab-btn ${paymentForm.type === 'CREDIT' ? 'active' : ''}`}
                    onClick={() => setPaymentForm({ ...paymentForm, type: 'CREDIT' })}
                  >
                    CREDIT
                  </button>
                </div>

                <div className="form-row-sales">
                  <div className="form-col-sales">
                    <label>Amount (Rs.)</label>
                    <input 
                      type="number" 
                      step="0.01"
                      className="custom-input"
                      value={paymentForm.amount}
                      onChange={e => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-col-sales btn-col">
                    <button type="submit" className="btn btn-add-payment">Add Payment</button>
                  </div>
                </div>
              </form>

              {/* Payments List */}
              <div className="payments-list-section">
                {payments.length === 0 ? (
                  <div className="empty-state">No payments added yet.</div>
                ) : (
                  <table className="mini-table">
                    <thead>
                      <tr>
                        <th>Payment Type</th>
                        <th>Amount</th>
                        <th>Details</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((payment, index) => (
                        <tr key={index}>
                          <td>{payment.type}</td>
                          <td>Rs. {payment.amount.toFixed(2)}</td>
                          <td>
                            {payment.type === 'CREDIT' ? `CREDIT #${payment.chequeId || 'N/A'}` : '-'}
                          </td>
                          <td>
                            <button className="btn-remove-mini" onClick={() => removePayment(index)} type="button">×</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Balance Display */}
              <div className="balance-summary">
                <div className="balance-row">
                  <span>Total Paid:</span>
                  <strong>Rs. {getTotalPaid().toFixed(2)}</strong>
                </div>
                <div className="balance-row">
                  <span>Balance / Change:</span>
                  <strong className={getBalance() >= 0 ? 'positive' : 'negative'}>
                    Rs. {Math.abs(getBalance()).toFixed(2)}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer className="modal-footer">
          <button className="btn btn-cancel" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-save" onClick={handleSubmit} disabled={isSaving || cart.length === 0}>
            {isSaving ? 'Processing...' : 'Complete Sale'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default CreateSaleModal;